import { useMemo, useState } from 'react';

import { authUserService } from '@store/AuthUser/services/authUserService';

import type { TaskFormActions, TaskFormState } from '../types';
import {
    applyEndDate,
    applyPatientDataField,
    applyStartDate,
    applyUrgency,
    createInitialTaskForm,
    resetTaskForm,
    seedTaskForm,
} from '../utils/taskFormState';

export function useTaskFormState(): { state: TaskFormState; actions: TaskFormActions } {
    const [state, setState] = useState<TaskFormState>(() => createInitialTaskForm(authUserService.getTimezone()));

    // мемоизируем: actions — зависимость useEffect в useTaskInit; сеттеры зависят только от стабильного setState
    const actions: TaskFormActions = useMemo(
        () => ({
            reset: () => setState(prev => resetTaskForm(prev, authUserService.getTimezone())),
            seed: task => setState(prev => seedTaskForm(prev, task)),
            setUserPermissions: (authUserId, assistant) =>
                setState(prev => ({
                    ...prev,
                    authUserId,
                    authorsAssistantId: assistant?.authorsAssistantId,
                    requestersAssistantId: assistant?.requestersAssistantId,
                    executorsAssistantId: assistant?.executorsAssistantId,
                })),
            setParticipant: (name, userId) => setState(prev => ({ ...prev, [`${name}Id`]: userId })),
            setExecutors: userIds => setState(prev => ({ ...prev, executorIds: userIds })),
            setMedicalCaseIds: medicalCaseIds => setState(prev => ({ ...prev, medicalCaseIds })),
            setUrgency: urgency => setState(prev => applyUrgency(prev, urgency)),
            setDescriptionField: (name, value) => setState(prev => ({ ...prev, [name]: value })),
            setPatientDataField: (name, value) => setState(prev => applyPatientDataField(prev, name, value)),
            setStartDate: value => setState(prev => applyStartDate(prev, value)),
            setEndDate: value => setState(prev => applyEndDate(prev, value)),
            setRejectionComment: value => setState(prev => ({ ...prev, rejectionComment: value })),
            setStatus: (status, actualExecutorId) =>
                setState(prev => ({ ...prev, status, actualExecutorId: actualExecutorId ?? prev.actualExecutorId })),
            toggleEditMode: () => setState(prev => ({ ...prev, isEditMode: !prev.isEditMode })),
        }),
        [],
    );

    return { state, actions };
}
